import React, { useState, useEffect } from "react";
import { format, addDays, subDays } from "date-fns";
import axios from "axios";
import { getAuthHeader } from "../../helpers/auth"; 
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  WarningAmber as WarningIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon
} from "@mui/icons-material";

const NovaDailyTracker = () => {
  const config = getAuthHeader();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // --- STATE FOR EDITING REMARKS ---
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [tempRemark, setTempRemark] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // Your personal Google Apps Script Web App URL
  const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxgaAYMBTQ15JZnVu09sApwsCgc3nOvpORwMCKa8m6W6LvY8RecfyEccJPstYkO0iPf/exec";

  const fetchJobsForDate = async (date) => {
    try {
      setLoading(true);
      const dateStr = format(date, "yyyy-MM-dd");
      
      const response = await axios.get("/api/schedule/history", {
        params: { dateRange: dateStr },
        ...config,
      });
      
      const todaysJobs = response.data.jobs.filter(
        (j) => format(new Date(j.scheduled_date), "yyyy-MM-dd") === dateStr
      );
      
      setJobs(todaysJobs);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobsForDate(selectedDate);
  }, [selectedDate]);

  const handleDateChange = (e) => {
    if (!e.target.value) return;
    const [year, month, day] = e.target.value.split('-');
    setSelectedDate(new Date(year, month - 1, day));
  };

  // --- SAVE REMARK TO GOOGLE SHEET & UPDATE UI ---
  const handleSaveRemark = async (scheduleId) => {
    setSavingRemark(true);
    try {
      const response = await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          schedule_id: scheduleId,
          remarks: tempRemark,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update the local state so the new remark shows up instantly without refresh
        const updatedJobs = jobs.map(job => 
          job.schedule_id === scheduleId ? { ...job, remarks: tempRemark } : job
        );
        setJobs(updatedJobs);
        setEditingRemarkId(null); // Close the edit box
      } else {
        alert("Error saving remark: " + result.error);
      }
    } catch (error) {
      console.error("Failed to save to Google Sheets", error);
      alert("Failed to connect to Google Sheets. Check console for details.");
    } finally {
      setSavingRemark(false);
    }
  };

  // Helper functions
  const formatTime = (dateString) => {
    if (!dateString) return null;
    return format(new Date(dateString), "h:mm a");
  };

  // Extract valid image URL for clickable viewing
  const getFirstPhotoUrl = (photoData, basePath) => {
    if (!photoData) return null;
    let photoStr = "";
    
    if (typeof photoData === "string" && photoData.length > 5) {
      photoStr = photoData;
    }
    
    try {
      const parsed = JSON.parse(photoData);
      if (Array.isArray(parsed) && parsed.length > 0) {
        photoStr = parsed[0]; // Get first photo
      }
    } catch (e) {
      // Ignore parse error, fallback to string check
    }
    
    if (!photoStr || photoStr.length < 5) return null;
    if (photoStr.startsWith("http")) return photoStr;
    
    photoStr = photoStr.replace(/["\[\]]/g, ""); 
    return `https://app.sowashusa.com/uploads/${basePath}/${photoStr.split("/").pop()}`;
  };

  // --- SMART AI ANALYTICS ENGINE FOR REMARKS ---
  const generateSmartRemarks = (job) => {
    if (job.status === "scheduled") return [{ text: "No Job", type: "neutral" }];
    
    let anomalies = [];

    const beforeUrl = getFirstPhotoUrl(job.before_photos || job.before_photo_url, "job-photos");
    const afterUrl = getFirstPhotoUrl(job.after_photos || job.after_photo_url, "job-photos");
    const tbtUrl = getFirstPhotoUrl(job.temp_voltage_photos, "job-photos");

    if (!beforeUrl) anomalies.push({ text: "Missing Before Photo", type: "error" });
    if (!afterUrl && job.completed_at) anomalies.push({ text: "Missing After Photo", type: "error" });
    if (!tbtUrl) anomalies.push({ text: "Missing TBT", type: "warning" });

    const start = job.started_at || job.reached_location_at;
    const end = job.completed_at || job.after_photos_at;

    if (start && end) {
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();
      const diffMinutes = Math.round((endTime - startTime) / 60000);

      if (diffMinutes < 0) {
        anomalies.push({ text: "Invalid Timestamps", type: "error" });
      }
    }

    if (anomalies.length === 0) {
      return [{ text: "Proper", type: "success" }];
    }

    return anomalies;
  };

  // Cell Renderers
  const renderStatus = (status) => {
    if (!status || status === "scheduled") return <span style={{ color: "#111827", fontWeight: "600" }}>No Job</span>;
    if (status === "completed") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#166534", fontWeight: "700" }}>
          <CheckCircleIcon style={{ fontSize: "18px", color: "#22C55E" }} /> Completed
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#92400E", fontWeight: "700" }}>
        <HourglassIcon style={{ fontSize: "18px", color: "#F59E0B" }} /> Pending
      </div>
    );
  };

  const renderPhotoStatus = (photoUrl, jobStatus) => {
    if (jobStatus === "scheduled") return <span style={{ color: "#111827", fontWeight: "600" }}>No Job</span>;
    
    if (photoUrl) {
      return (
        <div 
          onClick={() => window.open(photoUrl, "_blank")}
          style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: "6px", 
            color: "#166534", 
            fontWeight: "700", 
            cursor: "pointer", 
            padding: "4px 10px", 
            background: "#DCFCE7", 
            borderRadius: "8px", 
            border: "1px solid #22C55E",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => e.currentTarget.style.background = "#BBF7D0"}
          onMouseOut={(e) => e.currentTarget.style.background = "#DCFCE7"}
        >
          <ViewIcon style={{ fontSize: "16px" }} /> View
        </div>
      );
    }
    
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", color: "#991B1B", fontWeight: "700" }}>
        <CancelIcon style={{ fontSize: "18px", color: "#EF4444" }} /> Missing
      </div>
    );
  };

  const renderTime = (timeString, jobStatus) => {
    if (jobStatus === "scheduled") return <span style={{ color: "#111827", fontWeight: "600" }}>No Job</span>;
    if (timeString) {
      return <span style={{ fontWeight: "700", color: "#111827" }}>{formatTime(timeString)}</span>;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#991B1B", fontWeight: "700" }}>
        <CancelIcon style={{ fontSize: "18px", color: "#EF4444" }} /> Missing
      </div>
    );
  };

  // Table Styling
  const tableHeaderStyle = {
    padding: "16px 12px",
    background: "#f8f9fa",
    borderBottom: "2px solid #111827",
    borderRight: "1px solid #111827",
    fontWeight: "800",
    fontSize: "14px",
    color: "#000000",
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 10,
  };

  const tableCellStyle = {
    padding: "12px",
    borderBottom: "1px solid #d1d5db",
    borderRight: "1px solid #111827",
    fontSize: "14px",
    background: "#ffffff",
    textAlign: "center",
  };

  return (
    <div style={{ 
      padding: "24px", 
      background: "#ffffff", 
      height: "100vh", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden" 
    }}>
      
      {/* Date Navigation Header */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between", 
        marginBottom: "24px",
        paddingBottom: "16px",
        borderBottom: "2px solid #e5e7eb"
      }}>
        <h2 style={{ margin: 0, fontWeight: "900", fontSize: "24px", color: "#111827", textTransform: "uppercase" }}>
          Daily Job Tracker
        </h2>
        
        {/* Date Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button 
            onClick={() => setSelectedDate(subDays(selectedDate, 1))}
            style={{ padding: "8px 12px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <ArrowBackIcon style={{ fontSize: "16px", color: "#4b5563" }} />
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "white", border: "2px solid #111827", borderRadius: "8px", padding: "6px 12px" }}>
            <CalendarIcon style={{ fontSize: "18px", color: "#111827" }} />
            <input 
              type="date" 
              value={format(selectedDate, "yyyy-MM-dd")}
              onChange={handleDateChange}
              style={{ border: "none", outline: "none", fontWeight: "700", fontSize: "16px", color: "#000000", cursor: "pointer" }}
            />
          </div>

          <button 
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
            style={{ padding: "8px 12px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center"}}
          >
            <ArrowForwardIcon style={{ fontSize: "16px", color: "#4b5563" }} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "18px", color: "black" }}>
          Loading Tracker Data...
        </div>
      ) : (
        <div style={{ 
          flex: 1, 
          overflow: "auto", 
          border: "2px solid #111827", 
          borderRadius: "8px" 
        }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...tableHeaderStyle, textAlign: "left", borderLeft: "none" }}>Site / Lead</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Before Photo</th>
                <th style={tableHeaderStyle}>After Photo</th>
                <th style={tableHeaderStyle}>TBT Photo</th>
                <th style={tableHeaderStyle}>Total Panels</th>
                <th style={tableHeaderStyle}>Arrival</th>
                <th style={tableHeaderStyle}>Work Start</th>
                <th style={tableHeaderStyle}>Work Done</th>
                <th style={tableHeaderStyle}>Exit</th>
                <th style={{ ...tableHeaderStyle, width: "300px" }}>Smart Remarks</th>
                <th style={{ ...tableHeaderStyle, borderRight: "none" }}>PDF Report</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const isScheduledOnly = job.status === "scheduled";
                const smartRemarks = generateSmartRemarks(job);

                const beforeUrl = getFirstPhotoUrl(job.before_photos || job.before_photo_url, "job-photos");
                const afterUrl = getFirstPhotoUrl(job.after_photos || job.after_photo_url, "job-photos");
                const tbtUrl = getFirstPhotoUrl(job.temp_voltage_photos, "job-photos");

                return (
                  <tr key={job.schedule_id} style={{ transition: "background 0.2s" }} onMouseOver={(e) => e.currentTarget.style.background = "#f9fafb"} onMouseOut={(e) => e.currentTarget.style.background = "white"}>
                    <td style={{ ...tableCellStyle, textAlign: "left", borderLeft: "none" }}>
                      <div style={{ fontWeight: "800", color: "#111827", fontSize: "15px" }}>
                        {job.site_name || "Unknown Site"}
                      </div>
                      <div style={{ fontSize: "12px", color: "#4B5563", fontWeight: "600", marginTop: "2px" }}>
                        {job.team_lead_name || "No Lead Assigned"}
                      </div>
                    </td>
                    
                    <td style={tableCellStyle}>{renderStatus(job.status)}</td>
                    
                    <td style={tableCellStyle}>{renderPhotoStatus(beforeUrl, job.status)}</td>
                    <td style={tableCellStyle}>{renderPhotoStatus(afterUrl, job.status)}</td>
                    <td style={tableCellStyle}>{renderPhotoStatus(tbtUrl, job.status)}</td>
                    
                    <td style={tableCellStyle}>
                      {isScheduledOnly ? (
                        <span style={{ fontWeight: "600", color: "black" }}>Not Started</span>
                      ) : (
                        <span style={{ fontWeight: "800", fontSize: "16px", color: "black" }}>{job.total_panels_cleaned || 'N/A'}</span>
                      )}
                    </td>
                    <td style={tableCellStyle}>{renderTime(job.reached_location_at, job.status)}</td>
                    <td style={tableCellStyle}>{renderTime(job.started_at, job.status)}</td>
                    <td style={tableCellStyle}>{renderTime(job.after_photos_at || job.completed_at, job.status)}</td>
                    <td style={tableCellStyle}>{renderTime(job.completed_at, job.status)}</td>
                    
                    {/* --- INTERACTIVE REMARKS CELL --- */}
                    <td style={{ ...tableCellStyle, textAlign: "left", fontSize: "13px" }}>
                      {editingRemarkId === job.schedule_id ? (
                        // Edit Mode
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <textarea 
                            value={tempRemark}
                            onChange={(e) => setTempRemark(e.target.value)}
                            rows={2}
                            style={{ 
                              width: "100%", padding: "6px", borderRadius: "6px", 
                              border: "1px solid #3B82F6", fontSize: "13px", resize: "none", outline: "none"
                            }}
                            placeholder="Type manual remark here..."
                            autoFocus
                          />
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button 
                              onClick={() => handleSaveRemark(job.schedule_id)}
                              disabled={savingRemark}
                              style={{ 
                                background: "#10B981", color: "white", border: "none", 
                                padding: "4px 10px", borderRadius: "6px", cursor: "pointer", fontWeight: "700" 
                              }}
                            >
                              {savingRemark ? "..." : "Save"}
                            </button>
                            <button 
                              onClick={() => setEditingRemarkId(null)}
                              style={{ 
                                background: "#EF4444", color: "white", border: "none", 
                                padding: "4px 10px", borderRadius: "6px", cursor: "pointer", fontWeight: "700" 
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div 
                          style={{ cursor: "pointer", minHeight: "24px" }} 
                          onClick={() => {
                            setTempRemark(job.remarks || "");
                            setEditingRemarkId(job.schedule_id);
                          }}
                          title="Click to edit remark"
                        >
                          {job.remarks ? (
                            <div style={{ color: "#374151", fontWeight: "600", padding: "6px", background: "#F3F4F6", borderRadius: "6px" }}>
                              ✏️ {job.remarks}
                            </div>
                          ) : (
                            smartRemarks.map((remark, idx) => (
                              <div 
                                key={idx} 
                                style={{ 
                                  marginBottom: "4px", display: "flex", alignItems: "flex-start", gap: "6px",
                                  color: remark.type === "error" ? "#DC2626" : 
                                         remark.type === "warning" ? "#D97706" :
                                         remark.type === "success" ? "#166534" : "#374151"
                                }}
                              >
                                {remark.type === "error" && <WarningIcon style={{ fontSize: "16px", marginTop: "1px" }} />}
                                {remark.type === "success" && "✅"}
                                {remark.type === "warning" && "⚠️"}
                                <span>{remark.text}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </td>

                    <td style={{ ...tableCellStyle, borderRight: "none" }}>
                      {isScheduledOnly ? (
                        <span style={{ fontWeight: "600", color: "black" }}>Not Available</span>
                      ) : (
                        !job.completed_at && (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", color: "#991B1B", fontWeight: "700" }}>
                            <CancelIcon style={{ fontSize: "18px", color: "#EF4444" }} /> Missing
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}

              {jobs.length === 0 && (
                <tr>
                  <td colSpan="12" style={{ padding: "40px", textAlign: "center", fontSize: "16px", fontWeight: "600", color: "#6B7280" }}>
                    No field jobs scheduled for this date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NovaDailyTracker;